/**
 * ProvisionSection — Зөвшөөрлийн §7 «УРЬДЧИЛАН СЭРГИЙЛЭХ АРГА ХЭМЖЭЭ».
 *
 * HRT-ийн `_step7` (AT_Step7 + AT_Step7Checks + Poster)-ийн орлуулга.
 *
 * Хоёр үйлдэл:
 *   1. СОНГОХ   — лавлахаас (53 шаардлага) тухайн ажилд хамаарахыг тэмдэглэнэ
 *   2. ШАЛГАХ   — ээлж бүрд ажилд оруулагч БА ажил гүйцэтгэгч ХОЁУЛАА
 *                 тус тусдаа шалгаж баталгаажуулна (давхар баталгаа)
 *
 * ⚠ Энэ нь зөвшөөрлийн хуудаснаас ТУСДАА хэвлэгддэг ХОЁР ДАХЬ ЦААС
 *   (эх систем: `Provision.rdlc`, жишээ файл «11-1131-384367.xls»).
 *   Талбай дээр ажил эхлэх бүрд гараар ч тэмдэглэгддэг тул хэвлэсэн хуудсанд
 *   нүд хоосон үлдэх боломжтой байх ёстой.
 *
 * Backend: /xab/hr-job-provisions (GET · POST · PUT)
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Modal, Input, Tooltip, message } from "antd";
import {
  getJobProvisions,
  setJobProvisions,
  checkJobProvision,
} from "src/api/high_risk";
import { xabHrRefApi } from "src/api/xabApi";
import { useUserContext } from "src/contexts/userContext";

/** Ээлжийн баганын өнгө — 4 ээлжийг нүдээр ялгахад. */
const SHIFT_TINT = ["#F8FAFC", "#F1F5F9", "#F8FAFC", "#F1F5F9"];

function PickModal({ open, chosen, onClose, onSave }) {
  const [all, setAll] = useState([]);
  const [sel, setSel] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSel(chosen);
    setQ("");
    xabHrRefApi
      .all()
      .then((d) => setAll(d?.provisions || []))
      .catch(() => setAll([]));
  }, [open, chosen]);

  const needle = q.trim().toLowerCase();
  const shown = needle
    ? all.filter((p) =>
        String(p.description || "").toLowerCase().includes(needle),
      )
    : all;

  const toggle = (lid) =>
    setSel((prev) =>
      prev.includes(lid) ? prev.filter((x) => x !== lid) : [...prev, lid],
    );

  return (
    <Modal
      open={open}
      title="Урьдчилан сэргийлэх арга хэмжээ сонгох"
      onCancel={onClose}
      onOk={async () => {
        setBusy(true);
        try {
          await onSave(sel);
          onClose();
        } finally {
          setBusy(false);
        }
      }}
      okText={`Хадгалах (${sel.length})`}
      cancelText="Болих"
      confirmLoading={busy}
      width={780}
    >
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <Input
          allowClear
          size="small"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Шаардлагын текстээр хайх…"
        />
        {/* Хуучин системд `s7_provUpdateAll` — бүгдийг нэг товшилтоор.
            Ихэнх засварын ажилд бараг бүх шаардлага хамаардаг. */}
        <Button
          size="small"
          onClick={() => setSel(shown.map((p) => p.legacy_id))}
          disabled={!shown.length}
        >
          Бүгдийг
        </Button>
        <Button size="small" onClick={() => setSel([])} disabled={!sel.length}>
          Цэвэрлэх
        </Button>
      </div>

      <div
        style={{
          maxHeight: 380,
          overflowY: "auto",
          border: "1px solid #EDEFF3",
          borderRadius: 8,
          padding: "4px 10px",
        }}
      >
        {shown.length === 0 ? (
          <div style={{ padding: 16, textAlign: "center", color: "#8A94A6" }}>
            Шаардлага олдсонгүй.
          </div>
        ) : (
          shown.map((p) => (
            <div
              key={p.legacy_id}
              style={{ padding: "4px 0", borderTop: "1px solid #F5F6F8" }}
            >
              <Checkbox
                checked={sel.includes(p.legacy_id)}
                onChange={() => toggle(p.legacy_id)}
              >
                <span style={{ fontSize: 12.5 }}>{p.description}</span>
              </Checkbox>
            </div>
          ))
        )}
      </div>
    </Modal>
  );
}

export default function ProvisionSection({ jobId, readonly }) {
  const { user } = useUserContext();
  const myTn = String(user?.info?.tn || user?.tn || "");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [picking, setPicking] = useState(false);

  const load = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      setData(await getJobProvisions(jobId));
    } catch (ex) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load]);

  const items = data?.items || [];
  const shifts = data?.shifts || [1, 2, 3, 4];
  const parties = data?.parties || [];
  const chosen = useMemo(
    () => items.map((x) => x.provision_legacy_id),
    [items],
  );

  const toggleCell = async (row, shift, party, next) => {
    try {
      await checkJobProvision({
        job_provision_id: row.id,
        shift_no: shift,
        party,
        checked: next,
      });
      load();
    } catch (ex) {
      message.error(ex?.response?.data?.message || "Тэмдэглэж чадсангүй.");
    }
  };

  if (!jobId) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-md p-2 mb-2">
        <div className="font-semibold text-slate-700 mb-1">
          <i className="fas fa-shield-alt mr-1" />
          Урьдчилан сэргийлэх арга хэмжээ (§7)
        </div>
        <div style={{ fontSize: 12, color: "#8A94A6" }}>
          Нарядаа эхлээд хадгалсны дараа арга хэмжээгээ сонгоно.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-2 mb-2">
      <div className="font-semibold text-slate-700 mb-2 flex items-center justify-between">
        <span>
          <i className="fas fa-shield-alt mr-1" />
          Урьдчилан сэргийлэх арга хэмжээ (§7)
          <span style={{ fontWeight: 400, fontSize: 12, color: "#8A94A6" }}>
            {" "}
            · {items.length} шаардлага
          </span>
        </span>
        <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {loading ? (
            <span style={{ fontSize: 12, color: "#8A94A6" }}>ачааллаж…</span>
          ) : null}
          {!readonly ? (
            <Button size="small" type="link" onClick={() => setPicking(true)}>
              <i className="fas fa-tasks" style={{ marginRight: 4 }} />
              Сонгох
            </Button>
          ) : null}
        </span>
      </div>

      {data?.unavailable ? (
        <div
          style={{
            fontSize: 12,
            color: "#92400E",
            background: "#FEF3C7",
            padding: "6px 8px",
            borderRadius: 6,
            marginBottom: 8,
          }}
        >
          Хүснэгт бэлэн болоогүй байна — <code>create_hr_job_provision.sql</code>{" "}
          миграцыг ажиллуулна уу.
        </div>
      ) : null}

      {items.length === 0 ? (
        <div style={{ fontSize: 12, color: "#B0B7C3" }}>
          Хараахан сонгоогүй — «Сонгох» дарж тухайн ажилд хамаарах арга
          хэмжээг тэмдэглэнэ.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{ width: "100%", fontSize: 11.5, borderCollapse: "collapse" }}
          >
            <thead>
              <tr>
                <th
                  rowSpan={2}
                  style={{ width: 26, padding: 4, borderBottom: "1px solid #E3E7EC" }}
                >
                  №
                </th>
                <th
                  rowSpan={2}
                  style={{
                    textAlign: "left",
                    padding: 4,
                    borderBottom: "1px solid #E3E7EC",
                  }}
                >
                  Арга хэмжээ
                </th>
                {shifts.map((s, i) => (
                  <th
                    key={s}
                    colSpan={parties.length}
                    style={{
                      padding: 4,
                      background: SHIFT_TINT[i % SHIFT_TINT.length],
                      borderBottom: "1px solid #E3E7EC",
                      fontSize: 10.5,
                    }}
                  >
                    {s}-р ээлж
                  </th>
                ))}
              </tr>
              <tr>
                {shifts.flatMap((s, i) =>
                  parties.map((p) => (
                    <th
                      key={`${s}-${p.key}`}
                      title={p.label}
                      style={{
                        padding: "2px 4px",
                        width: 42,
                        fontWeight: 500,
                        fontSize: 9.5,
                        color: "#7C8796",
                        background: SHIFT_TINT[i % SHIFT_TINT.length],
                        borderBottom: "1px solid #E3E7EC",
                      }}
                    >
                      {/* Багана нарийн тул товчлол — бүтэн нэр tooltip-д */}
                      {p.key === "admitter" ? "Оруулагч" : "Гүйцэтгэгч"}
                    </th>
                  )),
                )}
              </tr>
            </thead>
            <tbody>
              {items.map((row, idx) => (
                <tr key={row.id} style={{ borderTop: "1px solid #F0F2F5" }}>
                  <td style={{ padding: 4, color: "#8A94A6" }}>{idx + 1}</td>
                  <td style={{ padding: 4, lineHeight: 1.35 }}>
                    {row.description}
                  </td>
                  {shifts.flatMap((s, i) =>
                    parties.map((p) => {
                      const cell = row.checks?.[s]?.[p.key];
                      const on = !!cell?.checked;
                      return (
                        <td
                          key={`${row.id}-${s}-${p.key}`}
                          style={{
                            textAlign: "center",
                            padding: 2,
                            background: SHIFT_TINT[i % SHIFT_TINT.length],
                          }}
                        >
                          <Tooltip
                            title={
                              on
                                ? `${p.label} · ${cell.check_user_tn || ""} · ${
                                    cell.check_date || ""
                                  }`
                                : p.label
                            }
                          >
                            <span>
                              <Checkbox
                                disabled={readonly}
                                checked={on}
                                onChange={(e) =>
                                  toggleCell(row, s, p.key, e.target.checked)
                                }
                              />
                            </span>
                          </Tooltip>
                        </td>
                      );
                    }),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {items.length ? (
        <div style={{ fontSize: 11, color: "#8A94A6", marginTop: 6 }}>
          Ээлж бүрд ажилд оруулагч ба ажил гүйцэтгэгч{" "}
          <b>хоёулаа</b> тэмдэглэнэ. Тэмдэглэсэн хүн, огноог систем өөрөө
          бүртгэнэ ({myTn ? `таны Б/Д ${myTn}` : "нэвтэрсэн хэрэглэгч"}).
        </div>
      ) : null}

      <PickModal
        open={picking}
        chosen={chosen}
        onClose={() => setPicking(false)}
        onSave={async (ids) => {
          try {
            await setJobProvisions(jobId, ids);
            message.success("Хадгаллаа.");
            load();
          } catch (ex) {
            message.error(
              ex?.response?.data?.message || "Хадгалж чадсангүй.",
            );
            throw ex;
          }
        }}
      />
    </div>
  );
}
